import React, { useState } from "react";
import Highstock from "highcharts/highstock";
import ReactHighcharts from "react-highcharts";
import Skeleton from "react-loading-skeleton";
import { RoundedSelect } from "../tailwind/atoms";
import { TrendsIcon } from "../../assets/images";

const Charts = ReactHighcharts.withHighcharts(Highstock);

const ClaimsTrends = ({
  data = {},
  loading = false,
  trendKeyMap = {
    "Week-on-Week": "week_on_week",
    "Month-on-Month": "month_on_month",
  },
  selectedTrend = "Week-on-Week",
}) => {
  const [metricType, setMetricType] = useState("amount"); // volume or amount
  const [selectedOption, setSelectedOption] = useState(selectedTrend);

  // Handle metric toggle
  const handleMetricToggle = (type) => setMetricType(type);

  // Handle dropdown change (RoundedSelect gives index)
  const handleTrendChange = (index) => {
    const value = Object.keys(trendKeyMap)[index];
    setSelectedOption(value);
  };

  const renderChart = () => {
    const trendData = data?.[trendKeyMap[selectedOption]] || {};

    if (!trendData || !trendData[metricType]) {
      return (
        <div className="w-full h-96 flex items-center justify-center text-gray-400">
          No data available
        </div>
      );
    }

    // Prepare series: Reimbursement, Cashless, Total
    const series = ["reimbursement", "cashless", "total"].map((key) => {
      const dataset = trendData[metricType][key] || {};
      return {
        name: key.charAt(0).toUpperCase() + key.slice(1),
        data: Object.values(dataset),
        color:
          key === "reimbursement"
            ? "#2168F3"
            : key === "cashless"
            ? "#00B37E"
            : "#F59E0B", // Total
      };
    });

    const categories = Object.keys(trendData[metricType]["total"] || {});

    if (!categories.length) {
      return (
        <div className="w-full h-96 flex items-center justify-center text-gray-400">
          No data available
        </div>
      );
    }

    const baseConfig = {
      chart: { type: "line" },
      title: { text: "", align: "left" },
      credits: { enabled: false },
      xAxis: {
        categories,
        title: { text: selectedOption },
      },
      yAxis: {
        title: { text: metricType === "volume" ? "Number of Claims" : "Amount (₹)" },
        allowDecimals: false,
      },
      tooltip: {
        shared: true,
        useHTML: true,
        formatter: function () {
          let tooltip = `<strong>${this.x}</strong><br/>`;
          this.points.forEach((p) => {
            const value =
              metricType === "amount"
                ? `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(p.y)}`
                : p.y;
            tooltip += `${p.series.name}: <strong>${value}</strong><br/>`;
          });
          return tooltip;
        },
      },
      series,
    };

    return <Charts config={baseConfig} />;
  };

  return (
    <div className="bg-white p-6 shadow-sm">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-base text-gray-700 leading-none flex gap-4">
          <img src={TrendsIcon} className="h-4 w-4" alt="Trends Icon" />
          <span className="font-fig-semibold">Overall Claim Trends</span>
        </h1>

        {/* Right side controls */}
        <div className="flex items-center gap-4">
          {/* Metric toggle */}
          <div className="flex bg-gray-100 rounded-md p-1 space-x-1 overflow-x-auto">
            {["volume", "amount"].map((type) => {
              const isActive = metricType === type;
              return (
                <button
                  key={type}
                  onClick={() => handleMetricToggle(type)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-sm transition-colors duration-200 focus:outline-none
                    ${isActive ? "bg-white text-black shadow-sm rounded-md" : "text-gray-500"}
                  `}
                >
                  {type === "volume" ? "Count" : "Value (₹)"}
                </button>
              );
            })}
          </div>
        </div>

        {/* Trend dropdown */}
        <RoundedSelect
          listClasses="w-56"
          selectOptions={Object.keys(trendKeyMap)}
          selectValues={[0, 1]}
          curvalue={selectedOption}
          selectHandler={handleTrendChange}
        />
      </div>

      {/* Chart */}
      {loading ? <Skeleton width="100%" height={400} /> : <div className="mt-4">{renderChart()}</div>}
    </div>
  );
};

export default ClaimsTrends;
